
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Container } from '@/components/ui/container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet';
import { Check, Mail } from 'lucide-react';

const RegistrationSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const name = searchParams.get('name') || '';
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    // Countdown for auto-redirect
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleGoToHome = () => {
    navigate('/');
  };

  const handleGoToEmail = () => {
    // Map of common email providers
    const emailProviders: Record<string, string> = {
      'gmail.com': 'https://mail.google.com',
      'yahoo.com': 'https://mail.yahoo.com',
      'hotmail.com': 'https://outlook.live.com',
      'outlook.com': 'https://outlook.live.com',
      'icloud.com': 'https://www.icloud.com/mail',
    };

    // Extract domain from email
    const domain = email.split('@')[1]?.toLowerCase();
    
    // If domain is in our map, open that URL, otherwise default to generic webmail link
    const emailUrl = domain ? emailProviders[domain] || `https://webmail.${domain}` : null;
    
    if (emailUrl) {
      window.open(emailUrl, '_blank');
    } else {
      // Fallback - just let them know they should check email
      alert('Ju lutemi kontrolloni emailin tuaj për të aktivizuar llogarinë.');
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Regjistrimi i Suksesshëm | Tregu.al</title>
        <meta name="description" content="Regjistrimi juaj në Treguim.org u krye me sukses" />
      </Helmet>

      <Navbar />
      
      <main className="flex-grow pt-16 pb-12 bg-gray-50">
        <Container>
          <div className="max-w-md mx-auto mt-8">
            <Card className="border-brand-300 shadow-md">
              <CardHeader className="space-y-1 text-center">
                <div className="flex justify-center">
                  <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                    <Check className="h-10 w-10 text-green-600" />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-center">Regjistrimi i Suksesshëm!</CardTitle>
                <CardDescription className="text-base">
                  Faleminderit për regjistrimin në Tregu.al
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="text-center py-4">
                  <h3 className="text-lg font-medium mb-2">Një hap i fundit...</h3>
                  <p className="text-gray-500">
                    {name ? `Përshëndetje ${name},` : 'Përshëndetje,'} kemi dërguar një email konfirmimi në <strong className="font-semibold text-brand-700">{email}</strong>.
                  </p>
                  <p className="text-gray-500 mt-2">
                    Ju lutemi klikoni në linkun e konfirmimit për të aktivizuar llogarinë tuaj dhe filluar përdorimin e Tregu.al.
                  </p>
                  
                  <div className="mt-6 flex flex-col space-y-3">
                    <Button 
                      onClick={handleGoToEmail}
                      className="w-full bg-brand-500 hover:bg-brand-600 flex items-center justify-center gap-2"
                    >
                      <Mail className="h-5 w-5" /> Kontrollo Emailin
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      onClick={handleGoToHome}
                      className="w-full"
                    >
                      Kthehu tek Faqja Kryesore ({countdown}s)
                    </Button>
                  </div>
                  
                  <div className="mt-6 text-sm text-gray-500 border-t pt-4">
                    <p>Nuk e gjeni emailin? Ju lutemi kontrolloni edhe dosjen spam ose prisni pak minuta që emaili të dorëzohet.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
      
      <Footer />
    </div>
  );
};

export default RegistrationSuccess;
